//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import Highcharts, {
  AxisLabelsFormatterContextObject,
  TimelineDataLabelsFormatterContextObject,
  TooltipFormatterContextObject,
} from 'highcharts';

import { readableNumber } from '@utils/readableNumber';

export function DECIMAL_CUSTOM_OPTIONS(
  readablePercentFilter: (n: number) => string,
  showLabels?: boolean,
): Highcharts.Options {
  const tooltipFormatter = function (this: TooltipFormatterContextObject) {
    const readableY = readablePercentFilter(this.point.y!);
    return `<strong>${this.x}</strong><br/>${this.point.series.name}: ${readableY}%`;
  };
  const yAxisFormatter = function (
    this: AxisLabelsFormatterContextObject<number>,
  ) {
    const readableValue = readablePercentFilter(this.value);
    return `${readableValue}%`;
  };
  const dataLabelFormatter = function (
    this: TimelineDataLabelsFormatterContextObject,
  ) {
    const readableValue = readablePercentFilter(this.point.y!);
    return `${readableValue}%`;
  };
  return {
    chart: {
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
    },
    plotOptions: showLabels
      ? {
          area: {
            dataLabels: {
              enabled: true,
              formatter: dataLabelFormatter,
              style: {
                color: '#18ae8f',
              },
            },
            enableMouseTracking: false,
          },
        }
      : {},
    tooltip: {
      formatter: tooltipFormatter,
    },
    xAxis: {
      tickmarkPlacement: 'on',
    },
    yAxis: {
      allowDecimals: true,
      labels: {
        // including this breaks the animation when the graph changes, fix this later
        formatter: yAxisFormatter,
      },
    },
  };
}

export function PRICE_CUSTOM_OPTIONS(
  readablePriceFilter: (n: number) => string,
  yAxisPriceFilter?: (n: number) => string,
): Highcharts.Options {
  const tooltipFormatter = function (this: TooltipFormatterContextObject) {
    const readableY = readablePriceFilter(this.point.y!);
    return `<strong>${this.x}</strong><br/>${this.point.series.name}: ${readableY}`;
  };
  yAxisPriceFilter = yAxisPriceFilter || readablePriceFilter;
  const yAxisFormatter = function (
    this: AxisLabelsFormatterContextObject<number>,
  ) {
    const readableValue = yAxisPriceFilter(this.value);
    return `${readableValue}`;
  };
  return {
    chart: {
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
    },
    tooltip: {
      formatter: tooltipFormatter,
    },
    xAxis: {
      tickmarkPlacement: 'on',
    },
    yAxis: {
      allowDecimals: true,
      labels: {
        // including this breaks the animation when the graph changes, fix this later
        formatter: yAxisFormatter,
      },
    },
  };
}

export function NUMBER_CUSTOM_OPTIONS(
  readableNumberFilter: (n: number) => string,
): Highcharts.Options {
  const tooltipFormatter = function (
    this: Highcharts.TooltipFormatterContextObject,
  ) {
    const readableY = readableNumberFilter(this.point.y!);
    return `<strong>${this.x}</strong><br/>${this.point.series.name}: ${readableY}`;
  };
  return {
    chart: {
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
    },
    tooltip: {
      formatter: tooltipFormatter,
    },
    xAxis: {
      tickmarkPlacement: 'on',
    },
    yAxis: {
      allowDecimals: false,
      labels: {
        formatter: undefined, // Rely on the default formatter.
      },
    },
  };
}

export function CUSTOM_OPTIONS(showLabels = false): Highcharts.Options {
  const dataLabelFormatter = function (
    this: Highcharts.TimelineDataLabelsFormatterContextObject,
  ) {
    return readableNumber(this.point.y!);
  };
  const chartOptions: Highcharts.Options = {
    chart: {
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
    },
    plotOptions: {},
    xAxis: {
      tickmarkPlacement: 'on',
    },
    yAxis: {
      allowDecimals: true,
    },
  };

  if (showLabels) {
    chartOptions.plotOptions = {
      area: {
        dataLabels: {
          enabled: true,
          formatter: dataLabelFormatter,
          style: {
            color: '#18ae8f',
          },
        },
        enableMouseTracking: false,
      },
    };
  }

  return chartOptions;
}

export function CUSTOM_OPTIONS_NO_FILL(): Highcharts.Options {
  return {
    chart: {
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
    },
    yAxis: {
      tickmarkPlacement: 'on',
      allowDecimals: true,
    },
    plotOptions: {
      area: {
        fillOpacity: 0,
      },
    },
  };
}
